import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Zap, Lock, Loader2, CheckCircle, X, Check } from "lucide-react";
import { toast } from "sonner";
import { API_URL } from "../config/api";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        toast.error('Invalid reset link');
        navigate('/login');
        return;
      }

      try {
        const response = await axios.post(`${API_URL}/auth/validate-reset-token`, {
          token
        });
        
        if (response.data.valid) {
          setTokenValid(true);
          setUserInfo(response.data.userInfo);
        } else {
          toast.error('Reset link expired or invalid');
          navigate('/login');
        }
      } catch (error) {
        toast.error('Invalid or expired reset link');
        setTimeout(() => navigate('/login'), 2000);
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate password strength
    const isValid = 
      password.length >= 8 &&
      /[a-z]/.test(password) &&
      /[A-Z]/.test(password) &&
      /[0-9]/.test(password) &&
      /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
    
    if (!isValid) {
      toast.error('Please meet all password requirements');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/reset-password`, {
        token,
        newPassword: password
      });

      if (response.data.success) {
        setSuccess(true);
        toast.success('Password reset successfully!');
        setTimeout(() => navigate('/login'), 3000);
      }
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to reset password');
      }
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{background: '#f8f9fa'}}>
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Validating reset link...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{background: '#f8f9fa'}}>
      <Card className="w-full max-w-md shadow-xl relative overflow-hidden rounded-t-2xl rounded-b-none">
        <CardHeader className="text-center pt-16 pb-6">
          <div className="flex justify-center mb-4">
            <div className="p-4 rounded-xl shadow-lg" style={{background: '#007AFF'}}>
              <Lock className="w-10 h-10 text-white" />
            </div>
          </div>
          <CardTitle className="text-3xl font-bold" style={{color: '#1a1a1a'}}>
            Reset Your Password
          </CardTitle>
          <p className="text-gray-600 mt-2">
            {success ? "Password Updated!" : "Create a new password"}
          </p>
          {userInfo && !success && (
            <p className="text-sm text-gray-500 mt-2">
              For: <span className="font-semibold">{userInfo.email}</span>
            </p>
          )}
        </CardHeader>

        <CardContent className="px-8 pb-8">
          {!success ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              {userInfo && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <p className="text-xs font-semibold text-blue-900 mb-1">Your Username:</p>
                  <p className="text-sm font-mono font-bold text-blue-700">{userInfo.username}</p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-semibold text-gray-700">
                  New Password *
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Create a strong password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  className="h-12 text-base border-2 border-gray-200 focus:border-blue-500"
                  autoFocus
                />
              </div>

              {/* Password Requirements */}
              <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                <p className="text-xs font-semibold text-gray-700 mb-2">Password Requirements:</p>
                <div className="flex items-center gap-2">
                  {password.length >= 8 ? <Check className="w-3 h-3 text-green-600" /> : <X className="w-3 h-3 text-red-500" />}
                  <span className={`text-xs ${password.length >= 8 ? 'text-green-700' : 'text-gray-600'}`}>8+ characters</span>
                </div>
                <div className="flex items-center gap-2">
                  {/[a-z]/.test(password) ? <Check className="w-3 h-3 text-green-600" /> : <X className="w-3 h-3 text-red-500" />}
                  <span className={`text-xs ${/[a-z]/.test(password) ? 'text-green-700' : 'text-gray-600'}`}>Lowercase</span>
                </div>
                <div className="flex items-center gap-2">
                  {/[A-Z]/.test(password) ? <Check className="w-3 h-3 text-green-600" /> : <X className="w-3 h-3 text-red-500" />}
                  <span className={`text-xs ${/[A-Z]/.test(password) ? 'text-green-700' : 'text-gray-600'}`}>Uppercase</span>
                </div>
                <div className="flex items-center gap-2">
                  {/[0-9]/.test(password) ? <Check className="w-3 h-3 text-green-600" /> : <X className="w-3 h-3 text-red-500" />}
                  <span className={`text-xs ${/[0-9]/.test(password) ? 'text-green-700' : 'text-gray-600'}`}>Number</span>
                </div>
                <div className="flex items-center gap-2">
                  {/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password) ? <Check className="w-3 h-3 text-green-600" /> : <X className="w-3 h-3 text-red-500" />}
                  <span className={`text-xs ${/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password) ? 'text-green-700' : 'text-gray-600'}`}>Symbol</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-sm font-semibold text-gray-700">
                  Confirm Password *
                </Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Re-enter password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading}
                  className="h-12 text-base border-2 border-gray-200 focus:border-blue-500"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 text-base font-semibold mt-6"
                style={{background: '#007AFF'}}
                disabled={loading || password.length < 8 || password !== confirmPassword}
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Resetting...
                  </span>
                ) : (
                  "Reset Password"
                )}
              </Button>
            </form>
          ) : (
            <div className="text-center py-8">
              <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              
              <h3 className="text-xl font-bold mb-2">Password Reset!</h3>
              <p className="text-sm text-gray-600 mb-6">
                Your password has been successfully reset.
              </p>

              <Button
                onClick={() => navigate('/login')}
                className="w-full h-12 text-base font-semibold"
                style={{background: '#007AFF'}}
              >
                Go to Login
              </Button>
            </div>
          )}
        </CardContent>
        
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 text-center">
          <p className="text-xs text-gray-400">
            © 2025 BookingFlows. All rights reserved.
          </p>
        </div>
      </Card>
    </div>
  );
};

export default ResetPassword;
